import { Controller } from "@hotwired/stimulus";
import { GroupProfileDomHelpers } from "../services/GroupProfileDomHelpers";
import { GroupProfileValidationStateService } from "../services/GroupProfileValidationStateService";

/**
 * Validation Controller for Product Group Profile Step
 *
 * Responsibilities:
 * - Validates form completeness (all required selections made)
 * - Validates attribute inputs (required fields, date/time formats)
 * - Displays validation errors and summaries
 * - Manages submit button state based on validation
 * - Handles individual input validation on blur/change
 *
 * Events listened:
 * - group-profile:validate-form - Validate entire form
 * - group-profile:validate-group - Validate specific group
 * - group-profile:clear-validation - Clear validation errors
 *
 * Events dispatched:
 * - group-profile:validation-state-changed - When validation state changes
 */
export default class ProductGroupProfileValidationController extends Controller {
  static values = {
    validationError: {
      type: String,
      default: "Please make a selection",
    },
    missingSelectionText: {
      type: String,
      default: "Missing Selection",
    },
    submissionErrorText: {
      type: String,
      default: "An error occurred while submitting your data",
    },
    missingSelectionDetailsText: {
      type: String,
      default: "{missing} of {total} selections missing",
    },
  };

  static targets = ["attributeError"];

  connect() {
    this.setupValidationListeners();

    this.element.addEventListener(
      "group-profile:validate-form",
      this.handleValidateForm.bind(this)
    );
    this.element.addEventListener(
      "group-profile:clear-validation",
      this.handleClearValidation.bind(this)
    );
  }

  setupValidationListeners() {
    const attributeInputs = this.element.querySelectorAll(
      '[name*="_attributes_"]'
    );

    attributeInputs.forEach((input) => {
      input.addEventListener("blur", (event) => {
        this.validateAttributes(event);
      });

      input.addEventListener("change", (event) => {
        this.validateAttributes(event);
      });
    });
  }

  handleValidateForm(event) {
    const { submissionAttempted } = event.detail;

    if (submissionAttempted) {
      GroupProfileValidationStateService.setSubmissionAttempted(true);
    }

    const isValid = this.isFormValid();

    if (!isValid) {
      if (submissionAttempted) {
        this.showValidationErrors();
      }
      event.preventDefault();
    } else {
      this.clearValidationErrors();
    }

    if (submissionAttempted) {
      GroupProfileValidationStateService.dispatchValidationStateChange(
        this.element
      );
    }
  }

  handleClearValidation() {
    this.clearValidationErrors();
    GroupProfileValidationStateService.reset();
  }

  isFormValid() {
    const allGroups = new Set();

    this.element.querySelectorAll("[data-group-id]").forEach((element) => {
      allGroups.add(element.dataset.groupId);
    });

    if (!this.areAccommodationsValid()) {
      return false;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const groupId of allGroups) {
      const groupValid = this.isGroupValid(groupId);
      const attributesValid = this.isGroupAttributesValid(groupId);

      if (!groupValid || !attributesValid) {
        return false;
      }
    }

    return true;
  }

  isGroupValid(groupId) {
    const selects = this.element.querySelectorAll(
      `.product-group-profile-select[data-group-id='${groupId}']`
    );
    const checkboxes = this.element.querySelectorAll(
      `.product-group-profile-checkbox[data-group-id='${groupId}']`
    );

    let selectedCount = 0;
    let requiredCount = 0;

    const summaryLabel = this.element.querySelector(
      `.product-group-profile-summary-label[data-group-id="${groupId}"]`
    );

    if (summaryLabel && summaryLabel.dataset.requiredCount) {
      requiredCount = parseInt(summaryLabel.dataset.requiredCount, 10) || 0;
      selectedCount = parseInt(summaryLabel.dataset.selectedCount, 10) || 0;
      return selectedCount >= requiredCount;
    }

    selects.forEach((s) => {
      requiredCount += 1;
      if (s.value && s.value !== "") {
        selectedCount += 1;
      }
    });

    checkboxes.forEach((c) => {
      if (c.checked) {
        selectedCount += 1;
      }
    });

    if (checkboxes.length > 0) {
      const indices = new Set();
      checkboxes.forEach((c) => {
        if (c.dataset.index) {
          indices.add(c.dataset.index);
        }
      });
      requiredCount = Math.max(requiredCount, indices.size);
    }

    return selectedCount >= requiredCount;
  }

  areAccommodationsValid() {
    const roomAssignmentContainers =
      GroupProfileDomHelpers.findRoomAssignmentContainers(this.element);

    if (roomAssignmentContainers.length === 0) {
      return true; // No accommodations required
    }

    let allRoomsSelected = true;

    roomAssignmentContainers.forEach((container) => {
      const selectedRoom = container.querySelector(
        ".border-green-500.bg-green-50"
      );
      if (!selectedRoom) {
        allRoomsSelected = false;
      }
    });

    return allRoomsSelected;
  }

  isGroupAttributesValid(groupId) {
    const requiredAttributeInputs = this.element.querySelectorAll(
      `[name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-required="true"]`
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const input of requiredAttributeInputs) {
      if (!input.value || input.value.trim() === "") {
        return false;
      }
    }

    const dateTimeInputs = this.element.querySelectorAll(
      `[name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="date"], [name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="datetime"], [name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="time"]`
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const input of dateTimeInputs) {
      if (
        input.value &&
        input.value.trim() !== "" &&
        !this.isValidDateTimeFormat(input)
      ) {
        return false;
      }
    }

    return true;
  }

  validateAttributes(event) {
    const input = event.target;
    const fieldName = input.name;
    const isRequired = input.dataset.attributeRequired === "true";
    const { attributeType } = input.dataset;

    this.clearAttributeError(fieldName);

    if (isRequired && (!input.value || input.value.trim() === "")) {
      this.showAttributeError(fieldName, "This field is required");
      return;
    }

    if (
      input.value &&
      input.value.trim() !== "" &&
      (attributeType === "date" ||
        attributeType === "datetime" ||
        attributeType === "time")
    ) {
      if (!this.isValidDateTimeFormat(input)) {
        this.showAttributeError(fieldName, "Please enter a valid date/time");
        return;
      }
    }

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:validation-changed"
    );
  }

  // eslint-disable-next-line class-methods-use-this
  isValidDateTimeFormat(input) {
    const value = input.value.trim();
    const { attributeType } = input.dataset;

    if (!value) return true; // Empty values are valid for optional fields

    switch (attributeType) {
      case "date":
        // HTML5 date input enforces YYYY-MM-DD format
        return (
          /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value))
        );
      case "datetime":
        // HTML5 datetime-local input enforces YYYY-MM-DDTHH:MM format
        return (
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) &&
          !Number.isNaN(Date.parse(value))
        );
      case "time":
        // HTML5 time input enforces HH:MM format
        return /^\d{2}:\d{2}$/.test(value);
      default:
        return true;
    }
  }

  showValidationErrors() {
    this.clearValidationErrors();

    const groupErrors = new Map();
    const allGroups = new Set();
    let hasErrors = false;

    if (!this.areAccommodationsValid()) {
      hasErrors = true;
      const roomAssignmentContainers =
        GroupProfileDomHelpers.findRoomAssignmentContainers(this.element);
      let missingRoomsCount = 0;

      roomAssignmentContainers.forEach((container) => {
        const hasSelection = container.querySelector(
          ".border-green-500.bg-green-50"
        );
        if (!hasSelection) {
          missingRoomsCount += 1;

          const headerDiv = container.querySelector(".bg-gray-50");
          if (headerDiv) {
            headerDiv.classList.add("bg-red-50", "border-red-200");
            headerDiv.classList.remove("bg-gray-50");
          }
        }
      });

      if (missingRoomsCount > 0) {
        groupErrors.set("accommodation_rooms", {
          groupName: "Zimmer",
          missing: missingRoomsCount,
          total: roomAssignmentContainers.length,
        });
      }
    }

    this.element.querySelectorAll("[data-group-id]").forEach((element) => {
      allGroups.add(element.dataset.groupId);
    });

    allGroups.forEach((groupId) => {
      const groupValid = this.isGroupValid(groupId);
      const attributesValid = this.isGroupAttributesValid(groupId);

      if (!groupValid) {
        hasErrors = true;
        this.addGroupValidationErrors(groupId, groupErrors);
      }

      if (!attributesValid) {
        hasErrors = true;
        this.addAttributeValidationErrors(groupId, groupErrors);
      }
    });

    if (hasErrors) {
      this.showValidationSummary(groupErrors);

      const validationSummary = this.element.querySelector(
        ".validation-summary"
      );
      if (validationSummary) {
        validationSummary.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }

  addGroupValidationErrors(groupId, groupErrors) {
    const selects = this.element.querySelectorAll(
      `.product-group-profile-select[data-group-id='${groupId}']`
    );
    const checkboxes = this.element.querySelectorAll(
      `.product-group-profile-checkbox[data-group-id='${groupId}']`
    );

    let missing = 0;
    let total = 0;

    selects.forEach((select) => {
      total += 1;
      if (!select.value || select.value === "") {
        missing += 1;
        this.addValidationErrorClass(select);
      }
    });

    if (checkboxes.length > 0) {
      const indices = new Set();
      let checkedCount = 0;

      checkboxes.forEach((c) => {
        if (c.dataset.index) {
          indices.add(c.dataset.index);
        }
        if (c.checked) {
          checkedCount += 1;
        }
      });

      total = Math.max(total, indices.size);
      missing = Math.max(0, total - checkedCount);

      if (missing > 0) {
        checkboxes.forEach((c) => this.addValidationErrorClass(c));
      }
    }

    if (missing > 0) {
      groupErrors.set(groupId, {
        groupName: this.getGroupName(groupId),
        missing,
        total,
      });
    }
  }

  addAttributeValidationErrors(groupId, groupErrors) {
    const requiredAttributeInputs = this.element.querySelectorAll(
      `[name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-required="true"]`
    );

    const dateTimeInputs = this.element.querySelectorAll(
      `[name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="date"], [name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="datetime"], [name*="resource_sku_${groupId}_"][name*="_attributes_"][data-attribute-type="time"]`
    );

    let missingAttributes = 0;
    let invalidAttributes = 0;

    requiredAttributeInputs.forEach((input) => {
      if (!input.value || input.value.trim() === "") {
        missingAttributes += 1;
        this.showAttributeError(input.name, "This field is required");
      }
    });

    dateTimeInputs.forEach((input) => {
      if (
        input.value &&
        input.value.trim() !== "" &&
        !this.isValidDateTimeFormat(input)
      ) {
        invalidAttributes += 1;
        this.showAttributeError(input.name, "Please enter a valid date/time");
      }
    });

    const totalIssues = missingAttributes + invalidAttributes;
    if (totalIssues > 0) {
      let errorMessage = "";
      if (missingAttributes > 0 && invalidAttributes > 0) {
        errorMessage = `${missingAttributes} required field(s) missing, ${invalidAttributes} invalid format(s)`;
      } else if (missingAttributes > 0) {
        errorMessage = `${missingAttributes} required field(s) missing`;
      } else {
        errorMessage = `${invalidAttributes} invalid format(s)`;
      }

      groupErrors.set(`${groupId}_attributes`, {
        groupName: `${this.getGroupName(groupId)} (Required Information)`,
        missing: totalIssues,
        total: requiredAttributeInputs.length + dateTimeInputs.length,
        customMessage: errorMessage,
      });
    }
  }

  showValidationSummary(groupErrors) {
    const existingSummary = this.element.querySelector(".validation-summary");
    if (existingSummary) {
      existingSummary.remove();
    }

    const summaryDiv = document.createElement("div");
    summaryDiv.className =
      "validation-summary mb-4 p-4 bg-red-50 border border-red-200 rounded-md";

    summaryDiv.innerHTML = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            ${this.submissionErrorTextValue}
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${Array.from(groupErrors.values())
                .map((error) => {
                  const detailsText = this.missingSelectionDetailsTextValue
                    .replace(/\{missing\}/g, error.missing)
                    .replace(/\{total\}/g, error.total);
                  return `<li>${error.groupName}: ${detailsText}</li>`;
                })
                .join("")}
            </ul>
          </div>
        </div>
      </div>
    `;

    const title = this.element.querySelector("h2");
    if (title) {
      title.parentNode.insertBefore(summaryDiv, title.nextSibling);
    }
  }

  clearValidationErrors() {
    this.element
      .querySelectorAll(".product-group-profile-input-error")
      .forEach((input) => {
        input.classList.remove(
          "border-red-500",
          "product-group-profile-input-error"
        );
      });

    this.element
      .querySelectorAll(
        ".border.border-gray-200.rounded-lg.overflow-hidden.mb-6"
      )
      .forEach((container) => {
        const headerDiv = container.querySelector(".bg-red-50");
        if (headerDiv) {
          headerDiv.classList.remove("bg-red-50", "border-red-200");
          headerDiv.classList.add("bg-gray-50");
        }
      });

    const validationSummary = this.element.querySelector(".validation-summary");
    if (validationSummary) {
      validationSummary.remove();
    }

    GroupProfileValidationStateService.clearAllErrors();
  }

  showAttributeError(fieldName, message) {
    const errorElement = this.element.querySelector(
      `[data-product-group-profile-validation-target="attributeError"][data-field-name="${fieldName}"]`
    );
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove("hidden");
    }

    const input = this.element.querySelector(`[name="${fieldName}"]`);
    if (input) {
      input.classList.add("border-red-500");
      input.classList.remove("border-gray-300");
    }
  }

  clearAttributeError(fieldName) {
    const errorElement = this.element.querySelector(
      `[data-product-group-profile-validation-target="attributeError"][data-field-name="${fieldName}"]`
    );
    if (errorElement) {
      errorElement.textContent = "";
      errorElement.classList.add("hidden");
    }

    const input = this.element.querySelector(`[name="${fieldName}"]`);
    if (input) {
      input.classList.remove("border-red-500");
      input.classList.add("border-gray-300");
    }
  }

  // eslint-disable-next-line class-methods-use-this
  addValidationErrorClass(input) {
    input.classList.add("border-red-500", "product-group-profile-input-error");
  }

  getGroupName(groupId) {
    const selectWithGroupId = this.element.querySelector(
      `[data-group-id="${groupId}"]`
    );
    if (selectWithGroupId) {
      const detailsElement = selectWithGroupId.closest("details");
      if (detailsElement) {
        const groupNameSpan = detailsElement.querySelector(
          "summary span:first-child span:last-child"
        );
        if (groupNameSpan) {
          return groupNameSpan.textContent.trim();
        }
      }
    }

    return `Group ${groupId}`;
  }
}
